import { motion } from "framer-motion";
import {
  PhoneCall,
  PhoneIncoming,
  MessagesSquare,
  TrendingUp,
  Megaphone,
  Landmark,
  Workflow,
  Users,
  Globe,
  Zap,
  Check,
  BarChart3,
  ClipboardList,
  CalendarClock,
  AudioLines,
} from "lucide-react";
import { SectionHeader, EASE } from "@/components/Reveal";

const BLOCKS = [
  {
    icon: PhoneCall,
    title: "AI Phone Calls",
    items: ["Answer incoming calls", "Make outbound calls", "Book appointments", "Qualify leads", "Cold calling", "Voice assistants"],
  },
  {
    icon: PhoneIncoming,
    title: "AI Receptionist",
    items: ["24/7 answering service", "Appointment scheduling", "Customer enquiries", "Call routing"],
  },
  {
    icon: MessagesSquare,
    title: "AI Customer Support",
    items: ["Website chatbot", "WhatsApp", "Messenger", "Instagram", "Email support", "Live chat"],
  },
  {
    icon: TrendingUp,
    title: "Sales Automation",
    items: ["Lead generation", "CRM updates", "Proposal creation", "Follow-up sequences", "Quote generation"],
  },
  {
    icon: Megaphone,
    title: "Marketing",
    items: ["Social media", "Content creation", "SEO", "Blogs", "Email campaigns", "Advertising"],
  },
  {
    icon: Landmark,
    title: "Finance",
    items: ["Invoice generation", "Expense tracking", "Cashflow", "Payroll assistance", "VAT reminders", "Reports"],
  },
  {
    icon: Workflow,
    title: "Operations",
    items: ["Task management", "Workflow automation", "Internal dashboards", "Business reporting", "Staff management", "SOP management"],
  },
  {
    icon: Users,
    title: "HR",
    items: ["Recruitment", "Onboarding", "Training", "Policies", "Performance tracking", "Employee documentation"],
  },
  {
    icon: Globe,
    title: "AI Websites",
    items: ["Build websites", "Update websites", "SEO optimisation", "Landing pages", "E-commerce", "Booking systems"],
  },
  {
    icon: BarChart3,
    title: "Analytics",
    items: ["Business reporting", "KPI dashboards", "Sales forecasting", "Performance insights", "Trend detection"],
  },
  {
    icon: ClipboardList,
    title: "Administration",
    items: ["Document management", "Data entry", "Email handling", "File organisation", "Meeting notes"],
  },
  {
    icon: CalendarClock,
    title: "Scheduling",
    items: ["Calendar management", "Appointment booking", "Meeting coordination", "Reminders and follow-ups", "Team availability"],
  },
  {
    icon: AudioLines,
    title: "Voice Calling",
    items: ["Natural voice conversations", "Inbound call handling", "Outbound campaigns", "Call summaries and transcripts", "CRM call logging"],
  },
];

const INTEGRATIONS = [
  "Google Workspace", "Microsoft 365", "WhatsApp", "Xero", "QuickBooks",
  "Stripe", "Calendars", "Emails", "CRMs", "APIs", "n8n", "Zapier",
  "Custom Integrations",
];

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="relative border-y border-white/5 bg-[#070707] py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeader
          label="What Alkyrvis Can Do"
          title={
            <>
              An Entire Company. <span className="gold-text">On Demand.</span>
            </>
          }
          sub="Thirteen departments of capability, ready to deploy into your business today."
        />

        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {BLOCKS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: EASE }}
              className="card-glow-hover glass-panel group rounded-2xl p-8"
              data-testid={`capability-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-gold/20 bg-gold/5 transition-[box-shadow,border-color] duration-500 group-hover:border-gold/50 group-hover:shadow-[0_0_25px_rgba(216,192,138,0.3)]">
                  <b.icon className="h-5 w-5 text-gold" />
                </div>
                <h3 className="font-display text-xl font-medium tracking-tight text-[#F5F5F5]">
                  {b.title}
                </h3>
              </div>
              <ul className="mt-6 space-y-2.5">
                {b.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-3 text-sm text-[#A3A3A3] transition-colors duration-300 group-hover:text-[#C9C9C9]"
                  >
                    <Check className="h-3.5 w-3.5 shrink-0 text-gold" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="card-glow-hover glass-panel group relative overflow-hidden rounded-2xl border-gold/20 p-8 md:col-span-2 xl:col-span-3"
            data-testid="capability-ai-automation"
          >
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(216,192,138,0.1),transparent_65%)] blur-2xl" />
            <div className="relative flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 shadow-[0_0_25px_rgba(216,192,138,0.2)]">
                <Zap className="h-5 w-5 text-gold" />
              </div>
              <div>
                <h3 className="font-display text-xl font-medium tracking-tight text-[#F5F5F5]">
                  AI Automation
                </h3>
                <p className="mt-1 text-sm text-[#A3A3A3]">
                  Connect everything together.
                </p>
              </div>
            </div>
            <div className="relative mt-7 flex flex-wrap gap-2.5">
              {INTEGRATIONS.map((t) => (
                <span
                  key={t}
                  className="cursor-default rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 font-mono text-[11px] tracking-wider text-[#A3A3A3] transition-[border-color,color,background-color] duration-300 hover:border-gold/40 hover:bg-gold/10 hover:text-gold"
                  data-testid={`integration-${t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
